import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, Eye, Heart, Award, Users, TrendingUp, Linkedin } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { IMAGES } from '../constants/images';

const About = () => {
  const leadership = [
    {
      name: 'CA Abhinav Kapoor',
      role: 'Finance Vertical Head',
      image: IMAGES.abhinav,
      linkedin: 'https://www.linkedin.com/in/ca-abhinav-kapoor-aborwar-2b083755/',
      description: 'A qualified Chartered Accountant (2012) and Law Graduate from the University of Rajasthan. With nearly a decade of experience in the finance sector, including 3 years as Credit Manager at ICICI Bank, Abhinav leads SME Funding and Project Loans with deep industry expertise.'
    },
    {
      name: 'CA Aneesh Patni',
      role: 'Subsidy Vertical Head',
      image: IMAGES.aneesh,
      linkedin: 'https://www.linkedin.com/in/aneesh-patni-88aborwar/',
      description: 'A qualified Chartered Accountant (2012) and Commerce Graduate from the University of Rajasthan. With 3 years of ERP consulting experience, Aneesh successfully manages both State and Central Government Subsidy Schemes, helping industries maximize their government benefits.'
    },
    {
      name: 'Mr. Jeetesh Dutt Mathur',
      role: 'Marketing Vertical Head',
      image: IMAGES.jeetesh,
      linkedin: 'https://www.linkedin.com/in/jeetesh-mathur-3a6b4441/',
      description: 'A Commerce Graduate with over 15 years of experience in retail finance. His career includes 7 years as Sales Head at Citi Financial and 8 years as AVP at Andromeda. Jeetesh leads the Retail Loans sector, managing relationships across banks and NBFCs.'
    }
  ];

  const values = [
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Mission',
      description: 'To empower businesses with accessible financial solutions and government benefits, enabling sustainable growth and economic development.'
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: 'Vision',
      description: 'To be the most trusted financial consultancy firm in India, known for integrity, expertise, and client success in subsidy and finance advisory.'
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: 'Values',
      description: 'Transparency, integrity, client-first approach, and commitment to excellence guide every interaction and decision we make.'
    }
  ];

  const milestones = [
    { year: '2014', title: 'Foundation', description: 'Established with a vision to simplify financial consultancy' },
    { year: '2016', title: 'Expansion', description: 'Extended services to include government subsidies' },
    { year: '2018', title: 'Growth', description: 'Crossed 200+ successful client engagements' },
    { year: '2020', title: 'D2D Model', description: 'Launched comprehensive Documentation to Disbursement service' },
    { year: '2024', title: 'Leadership', description: '500+ clients served with 98% success rate' }
  ];

  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-[#f8fafc]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-white rounded-full text-sm font-medium text-[#3b5998] mb-6 shadow-sm">
                About Us
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#0f172a] leading-tight mb-6">
                Trusted Financial Consultancy
                <span className="gradient-text block">Since 2014</span>
              </h1>
              <p className="text-lg text-[#64748b] leading-relaxed mb-6">
                Thesaurus of Finance is a business consultancy firm providing expert guidance 
                in Finance and Government Subsidies. Led by experienced Chartered Accountants 
                and industry professionals, we have established ourselves as a reliable partner 
                for businesses seeking financial growth.
              </p>
              <p className="text-[#64748b] leading-relaxed">
                Our D2D model — Documentation to Disbursement — ensures complete, 
                end-to-end support throughout your financial journey. We take pride in 
                our transparent approach and commitment to delivering results.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src={IMAGES.foundersCover}
                alt="Our Founders"
                className="rounded-2xl shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-24" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-4">
              Our Foundation
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto">
              The principles that guide our work and define our commitment to clients.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <Card className="h-full border border-slate-100 hover:border-[#00bcd4]/30 transition-colors bg-white">
                  <CardContent className="p-8 text-center">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#3b5998] to-[#00bcd4] flex items-center justify-center text-white mx-auto mb-6">
                      {value.icon}
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-[#0f172a] mb-4">
                      {value.title}
                    </h3>
                    <p className="text-[#64748b] text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-[#f8fafc]" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-4">
              Our Journey
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto">
              A decade of growth, learning, and delivering value to our clients.
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-[#3b5998] to-[#00bcd4] hidden lg:block"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className={`flex items-center gap-8 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 inline-block">
                      <span className="stat-number text-3xl font-semibold">{milestone.year}</span>
                      <h3 className="font-heading text-lg font-semibold text-[#0f172a] mt-2">{milestone.title}</h3>
                      <p className="text-[#64748b] text-sm mt-1">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="w-4 h-4 rounded-full bg-gradient-to-br from-[#3b5998] to-[#00bcd4] hidden lg:block relative z-10"></div>
                  <div className="flex-1 hidden lg:block"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Press & Media Section */}
      <section className="py-24" data-testid="press-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-[#f1f5f9] rounded-full text-sm font-medium text-[#3b5998] mb-4">
              Press & Media
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-4">
              In The News
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto">
              Featured articles and press coverage highlighting our work in financial consultancy.
            </p>
          </motion.div>

          {/* Newspaper Clippings */}
          <div className="mb-16">
            <h3 className="font-heading text-xl font-semibold text-[#0f172a] mb-6 text-center">
              Recent Press Coverage
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden border border-slate-100"
              >
                <img
                  src={IMAGES.press1}
                  alt="Dainik Bhaskar - ICAI MSME Festival 2025"
                  className="w-full h-auto"
                />
                <div className="p-4 bg-gradient-to-r from-[#3b5998]/5 to-[#00bcd4]/5">
                  <p className="text-sm font-medium text-[#0f172a]">Dainik Bhaskar - Chittorgarh</p>
                  <p className="text-xs text-[#64748b]">29 June 2025</p>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden border border-slate-100"
              >
                <img
                  src={IMAGES.press2}
                  alt="MSME Day Festival 2025 Press Coverage"
                  className="w-full h-auto"
                />
                <div className="p-4 bg-gradient-to-r from-[#3b5998]/5 to-[#00bcd4]/5">
                  <p className="text-sm font-medium text-[#0f172a]">MSME Day Festival 2025</p>
                  <p className="text-xs text-[#64748b]">International MSME Day Celebration</p>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Article Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'RIPS 2024: A Game Changer for Rajasthan Industries',
                source: 'Industry Insights',
                date: 'January 2025',
                excerpt: 'Expert analysis on how the new investment promotion scheme is transforming the industrial landscape of Rajasthan.'
              },
              {
                title: 'MSMEs Benefit from Government Subsidy Schemes',
                source: 'Business Today',
                date: 'December 2024',
                excerpt: 'How financial consultants are helping small businesses navigate complex subsidy application processes.'
              },
              {
                title: 'Youth Entrepreneurship Gets Boost with VYUPY',
                source: 'Startup Weekly',
                date: 'November 2024',
                excerpt: 'Young entrepreneurs find support through government schemes with expert guidance from consultancy firms.'
              }
            ].map((article, index) => (
              <motion.div
                key={article.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <Card className="h-full border border-slate-100 hover:border-[#00bcd4]/30 transition-colors bg-white">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-xs font-medium text-[#00bcd4]">{article.source}</span>
                      <span className="text-slate-300">•</span>
                      <span className="text-xs text-[#64748b]">{article.date}</span>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-[#0f172a] mb-3">
                      {article.title}
                    </h3>
                    <p className="text-[#64748b] text-sm leading-relaxed">
                      {article.excerpt}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-24" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-[#f1f5f9] rounded-full text-sm font-medium text-[#3b5998] mb-4">
              Leadership
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-4">
              Meet Our Team
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto">
              Experienced professionals committed to your financial success.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {leadership.map((leader, index) => (
              <motion.div
                key={leader.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <Card className="h-full overflow-hidden border border-slate-100 hover:border-[#00bcd4]/30 transition-all group bg-white">
                  <div className="relative h-72 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
                    <img
                      src={leader.image}
                      alt={leader.name}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-transparent to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="font-heading text-xl font-semibold text-white">{leader.name}</h3>
                      <p className="text-[#00bcd4] text-sm font-medium">{leader.role}</p>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-[#64748b] text-sm leading-relaxed mb-4">
                      {leader.description}
                    </p>
                    <a
                      href={leader.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#0077b5] text-sm font-medium hover:text-[#006097] transition-colors"
                    >
                      <Linkedin size={16} />
                      Connect on LinkedIn
                    </a>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/leadership"
              className="inline-flex items-center gap-2 text-[#3b5998] font-medium hover:text-[#00bcd4] transition-colors"
            >
              View Full Leadership Profiles →
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 bg-[#0f172a]" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: <Award />, number: '10+', label: 'Years of Excellence' },
              { icon: <Users />, number: '500+', label: 'Clients Served' },
              { icon: <TrendingUp />, number: '₹1000Cr+', label: 'Funds Facilitated' },
              { icon: <Target />, number: '98%', label: 'Success Rate' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-white/10 rounded-xl text-[#00bcd4] mb-4">
                  {stat.icon}
                </div>
                <p className="text-4xl lg:text-5xl font-semibold text-white font-mono mb-2">{stat.number}</p>
                <p className="text-slate-400 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
